/**
 * MOD-number normalization — the single source of truth for how a funding
 * tranche's MOD # is stored and displayed. Hoffman (the GC) formats MOD
 * numbers as zero-padded 3 digits ("024", not "24"); this normalizes a PM's
 * typed value to match before it's persisted (client decision, 2026-07-22).
 *
 * Lives in `shared` so the API (authoritative — applied on tranche
 * create/update in `apps/api/src/routes/pcos.ts`) and the web app
 * (`EditPcoModal`, reflected on blur so the PM sees what will save) apply
 * the identical rule.
 */

/**
 * Normalize a MOD # for storage: trims, then left-pads a purely-numeric
 * 1-3 digit value to 3 digits with leading zeros ("24" -> "024", "4" ->
 * "004"). Already-padded values ("024") and anything non-numeric or longer
 * than 3 digits (alphanumeric refs, composite refs) pass through trimmed,
 * unchanged — MOD # is free text end-to-end, never parsed as a number.
 * Empty / null / undefined normalizes to null (no MOD recorded).
 */
export function normalizeModNumber(input: string | null | undefined): string | null {
  const trimmed = (input ?? '').trim();
  if (trimmed === '') return null;
  return /^\d{1,3}$/.test(trimmed) ? trimmed.padStart(3, '0') : trimmed;
}
